import { createActions, createAction } from 'redux-actions';
import { ADVERT_FORM } from 'shared/constants/actionTypes';
import { OTHER_ITEM } from 'shared/constants/advertForm';
import { getRange, compareByTitle } from 'shared/helpers/arrays';
import { phoneNormalize } from 'shared/modules';
import colors from 'shared/constants/json/colors.json';
import API from 'shared/constants/api';
import {
  fetchAveragePrice,
  fetchAdvertAverage,
  normalizePlainOptionIDsToSelected,
} from '../helpers/advertForm';

const URL =
  process.env.AB_IP || process.env.API_SERVER_URL || `http://localhost:${process.env.PORT || 3000}`;
//
// General
//

export const clearForm = createAction(ADVERT_FORM.CLEAR);
export const setIsEdit = createAction(ADVERT_FORM.SET_IS_EDIT);
export const setIsClearBtn = createAction(ADVERT_FORM.SET_IS_CLEAR_BTN);
//
// Selected
//

const actions = createActions(
  {
    ADVERT_FORM: {
      SELECTED: Object.keys(ADVERT_FORM.SELECTED).reduce(
        (object, field) => ({ ...object, [field]: undefined }),
        {},
      ),
    },
  },
  { namespace: '__' },
);
// exporting selectedActions like this due to an extract-hoc package bug
export const selectedActions = actions.advertForm.selected;

//
// Data
//

export const setData = {
  models: createAction(ADVERT_FORM.DATA_MODELS_SET),
  modifications: createAction(ADVERT_FORM.DATA_MODIFICATIONS_SET),
  equipments: createAction(ADVERT_FORM.DATA_EQUIPMENTS_SET),
  years: createAction(ADVERT_FORM.DATA_YEARS_SET),
  generations: createAction(ADVERT_FORM.DATA_GENERATIONS_SET),
  series: createAction(ADVERT_FORM.DATA_SERIES_SET),
  drives: createAction(ADVERT_FORM.DATA_DRIVES_SET),
  gearboxes: createAction(ADVERT_FORM.DATA_GEARBOXES_SET),
  existingPhotos: createAction(ADVERT_FORM.DATA_EXISTING_PHOTOS_SET),
  existingPhotosNew: createAction(ADVERT_FORM.DATA_EXISTING_PHOTOS_SET_NEW),
  existingAdvertsNew: createAction(ADVERT_FORM.DATA_EXISTING_ADVERTS_SET_NEW),
  newAdvert: createAction(ADVERT_FORM.DATA_NEW_ADVERT),
  currentFormStep: createAction(ADVERT_FORM.DATA_CURRENT_FORM_STEP),
  averagePrice: createAction(ADVERT_FORM.DATA_AVERAGE_PRICE_SET),
  advertsAveragePrice: createAction(ADVERT_FORM.DATA_ADVERTS_AVERAGE_PRICE_SET),
  averagePriceParams: createAction(ADVERT_FORM.DATA_AVERAGE_PRICE_PARAMS_SET),
  showAveragePrice: createAction(ADVERT_FORM.DATA_SHOW_AVERAGE_PRICE_SET),
  warranty: createAction(ADVERT_FORM.DATA_WARRANTY_SET),
};

//
// Vocabulary
//

export const setFormVocabulary = createAction(ADVERT_FORM.VOCABULARY_SET);
export const clearVocabulary = createAction(ADVERT_FORM.VOCABULARY_CLEAR);

//
// Clear
//

export const clearNewPhotos = createAction(ADVERT_FORM.DATA_EXISTING_PHOTOS_SET_NEW);
export const clearNewAdverts = createAction(ADVERT_FORM.DATA_EXISTING_ADVERTS_SET_NEW);

//
// Thunks
//
const { set, clear, toggle } = selectedActions;

const sharedThunks = {
  setSelectedOptions: pickedOptions => (dispatch, getState) => {
    dispatch(clearNewPhotos({ clear: true }));
    dispatch(clearNewAdverts({ clear: true }));
    dispatch(clear.options());
    const optionsTree = getState().advertForm.vocabulary.options;
    const normalizedOptions = normalizePlainOptionIDsToSelected(pickedOptions, optionsTree);

    dispatch(set.options(normalizedOptions));
  },
};

export const carSetThunks = {
  ...sharedThunks,

  make: make => (dispatch, getState, { fetch }) => {
    dispatch(set.make(make));
    dispatch(carSetThunks.model(null));
    dispatch(setData.models(null));

    if (make && make.id >= 0) {
      return fetch(API.CMM.modelsByMake(make.id))
        .then(r => r.json())
        .then(models => {
          dispatch(setData.models(models.sort(compareByTitle)));
        });
    }

    return Promise.resolve();
  },

  model: model => (dispatch, getState, { fetch }) => {
    dispatch(set.model(model));
    dispatch(carSetThunks.year(null));
    dispatch(setData.generations(null));
    dispatch(setData.years(null));

    if (model) {
      return fetch(API.CMM.generationsByModel(model.id))
        .then(r => r.json())
        .then(generations => {
          const generationYearsUniq = new Set(
            generations
              .map(generation =>
                getRange(parseInt(generation.year_begin, 10), parseInt(generation.year_end, 10)),
              )
              .reduce((result, generationYears) => [...result, ...generationYears], []),
          ).toJSON();

          dispatch(setData.generations(generations));
          dispatch(setData.years(generationYearsUniq));
        });
    }

    return Promise.resolve();
  },

  year: year => dispatch => {
    dispatch(set.year(+year));
    dispatch(carSetThunks.generation(null));
  },

  generation: generation => (dispatch, getState, { fetch }) => {
    dispatch(set.generation(generation));
    dispatch(carSetThunks.series(null));
    dispatch(set.modification(null));
    dispatch(set.capacity(null));
    dispatch(setData.series(null));
    dispatch(set.body(null));

    if (generation && !generation.id) {
      fetch(API.CMM.allSeries())
        .then(r => r.json())
        .then(series => {
          dispatch(setData.series(series));
        });
      fetch(API.CMM.allDrives())
        .then(r => r.json())
        .then(drives => {
          dispatch(setData.drives(drives));
        });
      fetch(API.CMM.allGearboxes())
        .then(r => r.json())
        .then(gearboxes => {
          dispatch(setData.gearboxes(gearboxes));
        });
    } else if (generation) {
      return fetch(API.CMM.seriesByGeneration(generation.id))
        .then(r => r.json())
        .then(series => {
          dispatch(setData.series(series));
        });
    }

    return Promise.resolve();
  },

  series: series => (dispatch, getState, { fetch }) => {
    dispatch(set.series(series));
    dispatch(carSetThunks.engine(null));
    dispatch(setData.modifications(null));

    if (series) {
      return fetch(API.CMM.modificationsBySeries(series.id))
        .then(r => r.json())
        .then(modifications => {
          dispatch(setData.modifications(modifications));
        });
    }

    return Promise.resolve();
  },

  body: body => dispatch => {
    dispatch(set.body(body));
  },

  engine: engine => dispatch => {
    dispatch(set.engine(engine));
    dispatch(carSetThunks.drive(null));
  },

  drive: drive => dispatch => {
    dispatch(set.drive(drive));
    dispatch(carSetThunks.gearbox(null));
  },

  gearbox: gearbox => dispatch => {
    dispatch(set.gearbox(gearbox));
    dispatch(carSetThunks.modification(null));
  },

  mileage: mileage => dispatch => {
    dispatch(set.mileage(mileage));

    if (mileage) dispatch(carSetThunks.averagePrice());
  },

  averagePrice: (data = null) => async (dispatch, getState, { fetch }) => {
    const queryParams = data || getState().advertForm.selected;
    const averagePrice = await fetchAveragePrice(fetch, queryParams);
    dispatch(setData.averagePrice(averagePrice));

    if (data) {
      dispatch(setData.showAveragePrice(true));
      dispatch(set.regNumber(''));
      const advertsByAverage = await fetchAdvertAverage(fetch, data);

      dispatch(setData.averagePriceParams(queryParams));
      dispatch(setData.advertsAveragePrice(advertsByAverage));
    }
  },

  modification: modification => (dispatch, getState, { fetch }) => {
    dispatch(set.modification(modification));
    dispatch(setData.equipments(null));
    dispatch(setData.averagePrice(null));
    dispatch(carSetThunks.equipment(null));

    if (modification && modification.id) {
      dispatch(set.capacity(null));
      dispatch(set.power(null));

      return Promise.all([
        fetch(API.CMM.equipmentsByModification(modification.id))
          .then(r => r.json())
          .then(equipments => {
            dispatch(setData.equipments(equipments));
          }),
      ]);
    }

    return Promise.resolve();
  },

  equipment: equipment => dispatch => {
    dispatch(set.equipment(equipment));
    dispatch(clear.options());
    if (equipment && equipment.id) {
      dispatch(
        carSetThunks.setSelectedOptions(
          Object.keys(equipment.options).map(key => parseInt(key, 10)),
        ),
      );
    }
  },

  abroad: abroad => (dispatch, getState) => {
    const { advertForm } = getState();
    const { statuses } = advertForm.vocabulary;
    const { advertFields } = advertForm.selected;

    if (abroad) {
      statuses
        .filter(status => status.isNotCompatibleWithAbroad)
        .forEach(status => {
          if (!advertFields[status.id]) {
            dispatch(toggle.advertFields(status.id));
          }
        });
    }

    dispatch(set.drivenFrom(null));
    dispatch(set.abroad(abroad));
  },

  regNumber: regNumber => async (dispatch, getState, { fetch }) => {
    dispatch(set.regNumber(regNumber));
    if (!regNumber) return true;

    const carParams = await fetch(API.ADVERT.PARAMS_BY_REG_NUMBER(regNumber))
      .then(r => {
        if (r.status === 404) {
          dispatch(clearForm());
          throw r.statusText;
        }
        return r.json();
      })
      .then(res => ({
        make: res.make,
        model: res.model,
        year: res.year,
        generation: { id: null },
        body: res.body,
        engine: res.engine,
        capacity: res.capacity ? (res.capacity / 1000).toFixed(1) : null,
        color: res.color ? colors.find(item => item.id === res.color) : null,
      }))
      .catch(() => {
        dispatch(clearForm());
        Promise.resolve();
      });

    if (!carParams) return false;

    if (carParams.make)
      carParams.make = await fetch(API.ADVERT.PARAMS_BY_ID.make(carParams.make)).then(r =>
        r.json().then(data => data),
      );

    if (carParams.model)
      carParams.model = await fetch(API.ADVERT.PARAMS_BY_ID.model(carParams.model)).then(r =>
        r.json().then(data => data),
      );

    if (carParams.engine)
      carParams.engine = await fetch(API.ADVERT.PARAMS_BY_ID.engine(carParams.engine)).then(r =>
        r.json().then(data => data[0]),
      );

    if (carParams.body)
      carParams.body = await fetch(API.ADVERT.PARAMS_BY_ID.body(carParams.body)).then(r =>
        r.json().then(data => data[0]),
      );

    Object.keys(carParams).map(key => dispatch(set[key](carParams[key])));

    return true;
  },

  usedWarranty: usedWarranty => dispatch => {
    dispatch(set.usedWarranty(usedWarranty));
  },
};

export const otherTransportSetThunks = {
  make: make => (dispatch, getState, { fetch }) => {
    dispatch(set.make(make));
    dispatch(set.makeTitle(null));
    dispatch(otherTransportSetThunks.model(null));
    dispatch(setData.models(null));

    if (make && make.id >= 0) {
      return (make.id
        ? fetch(API.CMM.modelsByMake(make.id)).then(r => r.json())
        : Promise.resolve(null)
      ).then(models => {
        if (models) {
          dispatch(setData.models([OTHER_ITEM, ...models.sort(compareByTitle)]));
        }

        if (!models || !models.length) {
          dispatch(set.model(OTHER_ITEM));
          dispatch(setData.models([OTHER_ITEM]));
        }
      });
    }

    return Promise.resolve();
  },

  model: model => dispatch => {
    dispatch(set.model(model));
    dispatch(set.modelTitle(null));

    return Promise.resolve();
  },
};
//
// Adverts
//
export const setPhotosNew = ({ value, onAdd }) => (dispatch, getState, { fetch }) => {
  value.map((a, b) => {
    const dat = new FormData();
    dat.append('filearg', a);
    fetch(API.ADVERT.photosNew(), {
      method: 'POST',
      body: dat,
    })
      .then(packages => {
        if (packages.status === 200) {
          onAdd([a]);
          return packages.json();
        }

        return null;
      })
      .then(res => {
        if (res) {
          dispatch(setData.existingPhotosNew({ set: res }));
        }
      })
      .catch(err => {
        console.error(err, 'catch');
      });
  });
};
export const setAdvertsNew = ({ formState, onSubmit }) => (dispatch, getState, { fetch }) => {
  dispatch(setData.existingAdvertsNew({ set: formState }));
  const {
    advertForm: { data: { newExistingPhotos, newExistingAdverts = formState } = {} } = {},
    user: { user: { phones = [] } = {} } = {},
  } = getState();
  newExistingAdverts.driven_from = Number(newExistingAdverts.driven_from);
  newExistingAdverts.photos = newExistingPhotos;
  newExistingAdverts.photosToUpdate = [];
  newExistingAdverts.photosToDelete = [];
  if (!(newExistingAdverts.phones && newExistingAdverts.phones[0])) {
    newExistingAdverts.phones = phones.map(dat => dat.number);
  }
  // src/shared/hocs/advertForm/withPostAdvert.js
  onSubmit({
    ...newExistingAdverts,
  });
};
export const requestRemoveAdvertForm = (data, { enqueueNotification, intl }) => (
  dispatch,
  getState,
  { fetch },
) => {
  const {
    globals: { locale },
    user: { token },
  } = getState();

  return fetch(API.SUPPORT_SET_MAIL, {
    method: 'POST',
    body: JSON.stringify(data),
  })
    .then(async res => {
      const status = res.status;
      const dat = (await res.json()) || {};
      if (status === 200) {
        enqueueNotification(
          intl.formatMessage({
            id: dat.message || 'Ваше письмо отправлено в Службу поддержки',
          }),
        );
      } else {
        enqueueNotification(
          intl.formatMessage({
            id:
              dat.message ||
              'Извините, произошла ошибка на сервере, попробуйте повторить операцию позже',
          }),
        );
      }
    })
    .catch(err => {
      console.error(err, 'catch');
      enqueueNotification(
        intl.formatMessage({
          id: 'Извините, произошла ошибка на сервере, попробуйте повторить операцию позже',
        }),
      );
    });
};
export const requestVinAdvertForm = (
  { values, advert, callback },
  { enqueueNotification, intl },
) => (dispatch, getState, { fetch }) => {
  const {
    globals: { locale },
    user: { token, user },
  } = getState();
  return fetch(API.SET_VIN, {
    method: 'POST',
    headers: {
      Authorization: `JWT ${token}`,
    },
    body: JSON.stringify({
      advert: advert.id,
      user: user.id,
      vin: advert.vin,
      contact_name: values.name,
      contact_phone: phoneNormalize(
        (user.phones && user.phones[0] && user.phones[0].number) || values.phone,
      ),
      contact_email: values.email,
      make: advert.make.id,
      mileage: advert.mileage,
      comment: values.comment,
    }),
  })
    .then(async res => {
      const status = res.status;
      const dat = (await res.json()) || {};
      if (status === 201) {
        callback(dat.id);
      } else {
        enqueueNotification(
          intl.formatMessage({
            id:
              dat.message ||
              'Извините, произошла ошибка на сервере, попробуйте повторить операцию позже',
          }),
        );
      }
    })
    .catch(err => {
      console.error(err, 'catch');
      enqueueNotification(
        intl.formatMessage({
          id: 'Извините, произошла ошибка на сервере, попробуйте повторить операцию позже',
        }),
      );
    });
};
export const requestLeasingForm = (
  { values, advert, callback },
  { enqueueNotification, intl },
) => async (dispatch, getState, { fetch }) => {
  const { email: emailUser = '', phone: phoneUser = '', name: nameUser = '' } = values;
  const {
    id = '',
    make: { title: make = '' } = {},
    model: { title: model = '' } = {},
    year = '',
    price = [],
    mileage = '',
    characteristics: {
      engine: { title: engine = '' } = {},
      gearbox: { title: gearbox = '' } = {},
      drive: { title: drive = '' } = {},
    } = {},
    color: { title: color = '' } = {},
    location: { title: location = '' } = {},
    description = '',
    contact_name: contactName = '',
    author: { first_name: firstName = '' },
    photos = [],
    permalink: advertUrl = '',
    is_not_cleared: isNotCleared,
    vab_leasing_monthly_payment,
    vab_leasing_months_count,
  } = advert;
  const {
    globals: { locale },
    user: { token, user },
  } = getState();
  const priceFilter = price.filter(a => a.currency === 'uah');
  const phone =
    (await fetch(API.GET_PHONE(id))
      .then(async res => res.json())
      .catch(err => {
        console.error(err, 'catch');
        enqueueNotification(
          intl.formatMessage({
            id: 'Извините, произошла ошибка на сервере, попробуйте повторить операцию позже',
          }),
        );
      })) || [];
  await fetch(API.LEASING, {
    method: 'POST',
    headers: {},
    body: JSON.stringify({
      id,
      make,
      model,
      year,
      price: priceFilter && priceFilter[0] && priceFilter[0].value,
      mileage,
      engine,
      gearbox,
      drive,
      color,
      custom_cleared: !isNotCleared,
      description,
      seller_name: firstName || contactName,
      seller_phone: phoneNormalize(phone[0]),
      seller_email: '',
      contact_name: nameUser,
      contact_phone: phoneNormalize(phoneUser),
      contact_email: emailUser,
      location,
      photos: [...photos.map(a => a.image)],
      advert_url: `${URL}${advertUrl}`,
      vab_leasing_monthly_payment,
      vab_leasing_months_count,
    }),
  })
    .then(async res => {
      const status = res.status;
      const dat = (await res.json()) || {};
      if (status === 200) {
        callback();
        enqueueNotification(
          intl.formatMessage({
            id: dat.message || 'Успешно',
          }),
        );
      } else {
        enqueueNotification(
          intl.formatMessage({
            id:
              dat.message ||
              'Извините, произошла ошибка на сервере, попробуйте повторить операцию позже',
          }),
        );
      }
    })
    .catch(err => {
      console.error(err, 'catch');
      enqueueNotification(
        intl.formatMessage({
          id: 'Извините, произошла ошибка на сервере, попробуйте повторить операцию позже',
        }),
      );
    });
};
export const requestAutobooking = ({ autobookingId, callback }, { enqueueNotification, intl }) => (
  dispatch,
  getState,
  { fetch },
) => {
  fetch(`https://autobooking.com/widgets/available/order?station_id=${autobookingId}`)
    .then(async res => {
      const status = res.status;
      if (status === 200) {
        callback();
      } else {
        enqueueNotification(
          intl.formatMessage({
            id: 'Запись на СТО временно недоступна',
          }),
        );
      }
    })
    .catch(err => {
      console.error(err, 'catch');
      enqueueNotification(
        intl.formatMessage({
          id: 'Извините, произошла ошибка на сервере, попробуйте повторить операцию позже',
        }),
      );
    });
};



// WEBPACK FOOTER //
// ./src/shared/actions/advertForm.js